import { FormField } from '../services/formConfigService';

export interface RepeatingSection {
  sectionId: string;
  sectionName: string;
  tableName: string;
  triggerType: 'quantity' | 'manual' | 'api_response' | 'self';
  triggerFieldId: string;
  apiEndpoint?: string;
  minRows: number;
  maxRows: number;
  allowAddRemove: boolean;
  layout: 'table' | 'card';
  showRowNumbers: boolean;
  showDeleteButton: boolean;
  rowTitleTemplate: string;
  fields: FormField[];
  parentSectionId?: string;
  childSectionIds: string[];
  nestedLevel: number;
  allowNesting: boolean;
}

/**
 * Group fields by repeating section ID
 */
export const groupFieldsByRepeatingSection = (fields: FormField[]): RepeatingSection[] => {
  const sections = new Map<string, RepeatingSection>();
  
  fields.forEach(field => {
    if (field.isRepeatingSection && field.repeatingSectionId) {
      if (!sections.has(field.repeatingSectionId)) {
        sections.set(field.repeatingSectionId, {
          sectionId: field.repeatingSectionId,
          sectionName: field.repeatingSectionName || field.repeatingSectionId,
          tableName: field.tableName || '',
          triggerType: field.repeatingTriggerType || 'manual',
          triggerFieldId: field.repeatingTriggerFieldId || '',
          apiEndpoint: field.repeatingSectionApiEndpoint,
          minRows: field.repeatingMinRows || 0,
          maxRows: field.repeatingMaxRows || 100,
          allowAddRemove: field.repeatingAllowAddRemove !== false,
          layout: field.repeatingLayout || 'card', // Default to card for mobile-friendly
          showRowNumbers: field.repeatingShowRowNumbers !== false,
          showDeleteButton: field.repeatingShowDeleteButton !== false,
          rowTitleTemplate: field.repeatingRowTitleTemplate || 'Row {index}',
          fields: [],
          parentSectionId: field.repeatingParentSectionId,
          childSectionIds: field.repeatingChildSectionIds || [],
          nestedLevel: field.repeatingNestedLevel || 0,
          allowNesting: field.repeatingAllowNesting !== false
        });
      }
      sections.get(field.repeatingSectionId)!.fields.push(field);
    }
  });
  
  return Array.from(sections.values());
};

/**
 * Create an empty row for a repeating section
 */
export const createEmptyRow = (section: RepeatingSection, rowIndex: number): any => {
  const row: any = { 
    _rowIndex: rowIndex,
    uid: 'newid' // Backend will generate actual GUID
  };
  
  section.fields.forEach(field => {
    // Skip system fields and parent reference fields
    if (field.fieldType !== 'system' && !field.fieldName.toLowerCase().includes('_uid')) {
      row[field.fieldName] = field.defaultValue || '';
    }
  });
  
  return row;
};

/**
 * Get nested section key for data storage
 */
export const getNestedSectionKey = (
  childSectionId: string,
  parentSectionId: string,
  parentRowIndex: number
): string => {
  return `${childSectionId}_${parentSectionId}_${parentRowIndex}`;
};

/**
 * Check if a section should be visible based on trigger conditions
 */
export const isSectionVisible = (
  section: RepeatingSection,
  formData: any
): boolean => {
  // Self trigger is always visible
  if (section.triggerType === 'self') {
    return true;
  }
  
  // Manual trigger is always visible
  if (section.triggerType === 'manual') {
    return true;
  }
  
  // Quantity trigger - check if trigger field has a value
  if (section.triggerType === 'quantity' && section.triggerFieldId) {
    const triggerValue = formData[section.triggerFieldId];
    return triggerValue !== undefined && triggerValue !== null && triggerValue !== '';
  }
  
  // API response trigger - check if trigger field has been used
  if (section.triggerType === 'api_response' && section.triggerFieldId) {
    const triggerValue = formData[section.triggerFieldId];
    return triggerValue !== undefined && triggerValue !== null && triggerValue !== '';
  }
  
  return true;
};

/**
 * Build submission data for repeating sections
 */
export const buildRepeatingSectionSubmissionData = (
  sections: RepeatingSection[],
  repeatingSectionData: { [sectionId: string]: any[] },
  formData: any
): any => {
  const data: any = {};
  
  // Get all unique table names
  const allTables = new Set<string>();
  sections.forEach(section => {
    if (section.tableName) {
      allTables.add(section.tableName);
    }
  });
  
  // Initialize tables
  allTables.forEach(tableName => {
    data[tableName] = [];
  });
  
  // Process each section
  sections.forEach(section => {
    const rows = repeatingSectionData[section.sectionId] || [];
    
    rows.forEach((row, rowIndex) => {
      const rowData: any = {
        uid: 'newid',
        _rowIndex: rowIndex
      };
      
      // Find parent reference field (e.g., cde_placement_header_uid)
      const parentRefField = section.fields.find(f => 
        f.fieldName && f.fieldName.toLowerCase().includes('_uid') && 
        f.fieldName.toLowerCase().includes('header')
      );
      
      if (parentRefField) {
        rowData[parentRefField.fieldName] = 'parent';
      }
      
      // Add field values
      section.fields.forEach(field => {
        if (field.fieldType !== 'system' && field.fieldName) {
          const value = row[field.fieldName];
          rowData[field.fieldName] = value !== undefined ? value : (field.defaultValue || '');
        }
      });
      
      data[section.tableName].push(rowData);
    });
  });
  
  return data;
};
